/**
 * ≡, the one menu (docs/mockups/nav.html, Direction A with a sheet everywhere): the same
 * button in the same top-right spot on every screen, opening the same bottom sheet (on a
 * wide screen with a mouse, a popover under ≡: J4, ui/Sheet.tsx). What's
 * in it depends on where you are:
 *
 * - In a game: "This puzzle" first (Check this answer, Check the puzzle, Reveal this
 *   answer), then How to play, Settings, About. Never the install line (house rule).
 * - Elsewhere: "Add Sparrow to your home screen" first (tinted, with the Dart tile; only
 *   unless Sparrow is detected as installed, by pwa/install-state.ts `lineVisible`),
 *   then How to play, Settings, About Sparrow.
 *
 * How to play, Settings and About open in the same sheet. The sheet owns one history entry
 * and a view opened from the menu one more, so back steps the view → the menu → the page.
 */
import { localDateKey } from '@arrow/game-engine';
import { lazy, type ReactNode, type Ref, Suspense, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';

import { DevContext } from '../app/dev-context.ts';
import { navigate, useOverlay } from '../app/router.tsx';
import { settings, type Theme, THEMES, type TrackedSwitch, useSettings } from '../app/settings.ts';
import { type MenuScreen, track } from '../platform/analytics.ts';
import { setUsageCounts } from '../platform/consent.ts';
import { noteSurfaceShown } from '../pwa/install.ts';
import { readPlatform } from '../pwa/platform.ts';
import { lineVisible } from '../pwa/install-state.ts';
import { DartTile } from './DartMark.tsx';
import { Icon } from './icons.tsx';
import { useInstallFlow } from './install/Install.tsx';
import { openNotifySheet } from '../push/host.tsx';
import { NotificationSettings } from '../push/Settings.tsx';
import { Segmented, Sheet, SheetRoot, SheetTrigger, SwitchRow } from '../ui/index.ts';
import '../styles/nav.css';

const AppDevPanel = lazy(() => import('../dev/AppDevPanel.tsx'));

export interface MenuItem {
	label: string;
	onSelect: () => void;
	disabled?: boolean;
	icon?: ReactNode;
}

type View = 'menu' | 'howto' | 'settings' | 'about';

/** The build's version, stamped by vite.config.ts (`0.1.0+abc1234`). */
export const APP_VERSION: string = (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';

interface AppMenuProps {
	screen: MenuScreen;
	/** In a game: this puzzle's actions, first. */
	puzzle?: MenuItem[] | undefined;
	/** One quiet line under "This puzzle": what any of its actions costs ("Any of these ends a perfect solve."). */
	puzzleNote?: string | undefined;
	/** Dev mode (dev/mode.ts): the dev tools, last. */
	dev?: (() => void) | undefined;
	/** Outside a game: the install line may lead (first visit or not; never in a game). */
	install?: { todayKey?: string | undefined } | undefined;
	buttonRef?: Ref<HTMLButtonElement> | undefined;
}

export function AppMenu({ screen, puzzle, puzzleNote, dev, install, buttonRef }: AppMenuProps) {
	const [view, setView] = useState<View | null>(null);
	// Dev mode: every screen's menu reaches the dev tools (the board passes its own fuller panel as `dev`).
	const devControls = useContext(DevContext);
	const [appDev, setAppDev] = useState(false);
	const noteId = useId();
	const openDev = dev ?? (devControls ? () => setAppDev(true) : undefined);
	// ≡ itself, kept here as well as handed to the caller: on a wide screen with a mouse the
	// menu hangs under it (J4).
	const button = useRef<HTMLButtonElement | null>(null);
	const setButton = useCallback(
		(el: HTMLButtonElement | null) => {
			button.current = el;
			if (typeof buttonRef === 'function') buttonRef(el);
			else if (buttonRef) buttonRef.current = el;
		},
		[buttonRef],
	);
	const anchor = useCallback(() => button.current, []);
	// The sheet (ui/Sheet) owns the menu's history entry; a view opened from it adds one more.
	const close = useCallback(() => setView(null), []);

	// Back from How to play, Settings or About lands on the row that opened it (UX-083).
	const rows = useRef<Partial<Record<View, HTMLButtonElement | null>>>({});
	const cameFrom = useRef<View | null>(null);
	useEffect(() => {
		if (view !== 'menu' || !cameFrom.current) return;
		rows.current[cameFrom.current]?.focus({ preventScroll: true });
		cameFrom.current = null;
	}, [view]);
	const backToMenu = () => {
		cameFrom.current = view;
		setView('menu');
	};
	// How to play, Settings and About each own a history entry over the menu's, so back
	// (Android, the Safari swipe) steps to the menu first, as ‹ does (QA2-11).
	useOverlay(view !== null && view !== 'menu', backToMenu, { nested: true });

	const flow = useInstallFlow('menu');
	const p = flow.platform;
	// Not while Sparrow is detected as installed, nor inside an app's own browser (the way-out card on Today does that job).
	const showInstall = !!install && !p.inApp && lineVisible({ installed: flow.installed, hasRoute: flow.route !== null });

	const open = () => {
		setView('menu');
		track('menu_opened', { screen });
		if (showInstall) noteSurfaceShown('menu', install?.todayKey ?? localDateKey(new Date()));
	};
	const choose = (fn: () => void) => () => {
		setView(null);
		fn();
	};
	// Install opens Chrome's sheet or a guide of its own. Closing the menu steps history back,
	// and that step landing after the install had opened closed it again ("does nothing"). So
	// wait for the menu's entry to come off first; Chrome's prompt still has the tap's
	// activation (it lasts seconds).
	const chooseAfterClose = (fn: () => void) => () => {
		let done = false;
		const go = () => {
			if (done) return;
			done = true;
			window.removeEventListener('popstate', go);
			fn();
		};
		window.addEventListener('popstate', go);
		setView(null);
		window.setTimeout(go, 350);
	};
	const show = (v: View) => () => {
		setView(v);
		if (v === 'howto') track('how_to_play_opened', { from: screen });
	};
	const keep = (v: View) => (el: HTMLButtonElement | null) => {
		rows.current[v] = el;
	};

	let title: ReactNode = 'Sparrow';
	let body: ReactNode = null;
	if (view === 'menu') {
		const installLabel = menuLine(flow.route, p.desktop);
		const inGame = !!puzzle && puzzle.length > 0;
		const list = (
			<ul className="mn-list">
				{showInstall && (
					<li>
						<MenuRow
							hi
							icon={<DartTile />}
							label={installLabel}
							onClick={chooseAfterClose(flow.start)}
							testId="menu-install"
							chevron
						/>
					</li>
				)}
				<li>
					<MenuRow ref={keep('howto')} icon={<Icon.help />} label="How to play" onClick={show('howto')} chevron />
				</li>
				<li>
					<MenuRow ref={keep('settings')} icon={<Icon.sliders />} label="Settings" onClick={show('settings')} chevron />
				</li>
				<li>
					<MenuRow ref={keep('about')} icon={<Icon.info />} label="About Sparrow" onClick={show('about')} chevron />
				</li>
				{openDev && (
					<li>
						<MenuRow icon={<Icon.tools />} label="Dev tools" onClick={choose(openDev)} />
					</li>
				)}
			</ul>
		);
		body = inGame ? (
			<>
				<section className="mn-sec" role="group" aria-label="This puzzle">
					<p className="mn-k" aria-hidden="true">
						This puzzle
					</p>
					{/* Said once for the three, not under each (and read as each row's description). */}
					{puzzleNote && (
						<p className="mn-note" id={noteId}>
							{puzzleNote}
						</p>
					)}
					<ul className="mn-list">
						{puzzle.map((item) => (
							<li key={item.label}>
								<MenuRow icon={item.icon} label={item.label} describedBy={puzzleNote ? noteId : undefined} disabled={item.disabled} onClick={choose(item.onSelect)} />
							</li>
						))}
					</ul>
				</section>
				{/* "More", not "Sparrow": the sheet's own title already says that (VH-29). */}
				<section className="mn-sec" role="group" aria-label="More">
					<p className="mn-k" aria-hidden="true">
						More
					</p>
					{list}
				</section>
			</>
		) : (
			// One group, so no group name to read (UX-025).
			<div className="mn-sec">{list}</div>
		);
	} else if (view === 'howto') {
		title = 'How to play';
		body = <HowToPlay inGame={screen === 'game'} />;
	} else if (view === 'settings') {
		title = 'Settings';
		body = <SettingsBody onInstall={chooseAfterClose(flow.start)} onNotify={chooseAfterClose(() => openNotifySheet({ surface: 'settings' }))} />;
	} else if (view === 'about') {
		title = 'About Sparrow';
		body = <About />;
	}

	return (
		<>
			{appDev && devControls && (
				<Suspense fallback={null}>
					<AppDevPanel controls={devControls} onClose={() => setAppDev(false)} />
				</Suspense>
			)}
			{/* ≡ is the sheet's Radix trigger (aria-haspopup, aria-expanded, aria-controls; focus comes back to it). */}
			<SheetRoot open={view !== null}>
				<div className="menu">
					<SheetTrigger>
						<button ref={setButton} type="button" className="icon-btn menu-btn" aria-label="Menu" onClick={open} data-testid="menu-button">
							<Icon.menu />
						</button>
					</SheetTrigger>
				</div>
				<Sheet
					open={view !== null}
					title={title}
					onClose={close}
					onBack={view === 'menu' ? undefined : backToMenu}
					className="mn-sheet"
					testId={`sheet-${view}`}
					anchor={anchor}
				>
					{body}
				</Sheet>
			</SheetRoot>
			{flow.sheet}
		</>
	);
}

/**
 * A row in the menu: its icon, its label (the name) and a quieter line under it (the
 * description, read after a pause), and a chevron when it opens a view of its own.
 */
function MenuRow({
	ref,
	icon,
	label,
	describedBy,
	onClick,
	disabled,
	hi = false,
	chevron = false,
	testId,
}: {
	ref?: Ref<HTMLButtonElement>;
	icon?: ReactNode;
	label: string;
	describedBy?: string | undefined;
	onClick: () => void;
	disabled?: boolean | undefined;
	hi?: boolean;
	chevron?: boolean;
	testId?: string;
}) {
	const id = useId();
	return (
		<button
			ref={ref}
			type="button"
			className={hi ? 'mn-row hi' : 'mn-row'}
			disabled={disabled}
			onClick={onClick}
			aria-labelledby={`${id}-l`}
			aria-describedby={describedBy}
			data-testid={testId}
		>
			<span className={hi ? 'mn-ic tile' : 'mn-ic'}>{icon}</span>
			<span className="mn-lb">
				<span id={`${id}-l`}>{label}</span>
			</span>
			{chevron && <Chevron />}
		</button>
	);
}

/** The menu's install line: "Add Sparrow to your home screen" (the Dock, your computer). */
function menuLine(route: string | null, desktop: boolean): string {
	if (route === 'mac_safari') return 'Add Sparrow to your Dock';
	if (desktop) return 'Add Sparrow to your computer';
	return 'Add Sparrow to your home screen';
}

function Chevron() {
	return (
		<span className="mn-chev" aria-hidden="true">
			<Icon.chevron />
		</span>
	);
}

/** Three steps, each with its picture: clues in the grid, type to fill, keep a Flow going. */
export function HowToPlay({ inGame }: { inGame: boolean }) {
	return (
		<>
			<ol className="howto">
				<li>
					<svg viewBox="0 0 120 120" aria-hidden="true">
						<rect x="10" y="10" width="100" height="100" fill="var(--clue-bg)" />
						<rect x="22" y="34" width="76" height="9" fill="var(--clue-ink)" />
						<rect x="30" y="50" width="60" height="9" fill="var(--clue-ink)" />
						<g transform="translate(46 70) scale(1.15)">
							<path d="M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z" fill="var(--arrow-on)" />
						</g>
					</svg>
					<span>
						The clues sit in the grid.
						<small>Each arrow shows where its answer goes.</small>
					</span>
				</li>
				<li>
					<svg viewBox="0 0 120 120" aria-hidden="true">
						<rect x="8" y="34" width="104" height="52" fill="var(--cell)" stroke="var(--frame)" strokeWidth="4" />
						<path d="M42 34v52M78 34v52" stroke="var(--rule)" strokeWidth="3" />
						<text x="25" y="72" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="30" fill="var(--letter)">
							S
						</text>
						<text x="60" y="72" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="30" fill="var(--letter)">
							E
						</text>
						<rect x="80" y="36" width="30" height="48" fill="var(--path)" />
					</svg>
					<span>
						Type to fill.
					</span>
				</li>
				<li>
					<svg viewBox="0 0 120 120" aria-hidden="true">
						<rect x="10" y="10" width="100" height="100" fill="var(--cell)" stroke="var(--frame)" strokeWidth="4" />
						<rect x="20" y="30" width="80" height="12" rx="6" fill="var(--flow)" />
						<rect x="60" y="26" width="12" height="70" rx="6" fill="var(--flow)" />
						<rect x="20" y="78" width="52" height="12" rx="6" fill="var(--flow)" />
					</svg>
					<span>
						Keep a Flow going.
						<small>Solve crossing answers one after another to build a chain.</small>
					</span>
				</li>
			</ol>
			<ul className="howto-notes">
				<li>
					<b>A clue ending in “?”</b> is a pun or wordplay: don’t take it literally.
				</li>
				<li className="howto-keys">
					<b>On a keyboard:</b> type to fill, arrows move, Tab or Enter for the next clue, Space to switch direction, Esc to leave the
					grid.
				</li>
			</ul>
			<p className="mn-foot">
				<span>{inGame ? 'Stuck? Check and Reveal are in this menu.' : 'In a puzzle, Check and Reveal are in ≡.'}</span>
			</p>
		</>
	);
}

const SWITCHES: { key: TrackedSwitch; label: string; sub: string }[] = [
	{ key: 'sound', label: 'Sounds', sub: 'Small pops as you type and solve.' },
	{ key: 'haptics', label: 'Haptics', sub: 'On phones that support it.' },
	{ key: 'clock', label: 'Show the clock', sub: 'Your time is still recorded.' },
	{ key: 'largeClues', label: 'Larger clues', sub: 'Long clues show in full.' },
	{
		key: 'shareBoard',
		label: 'Coloured squares in shares',
		sub: 'Screen readers read every square aloud. Off sends just your time and stats.',
	},
];

const THEME_LABEL: Record<Theme, string> = { system: 'Match phone', light: 'Light', dark: 'Dark' };

/**
 * Theme: Match phone · Light · Dark, one choice of three (ui/Segmented.tsx: arrow
 * keys move and choose, Tab leaves). The page changes as you choose (app/theme.ts).
 */
function ThemeRow({ value }: { value: Theme }) {
	const id = useId();
	// On a computer it's the computer's setting, not a phone's.
	const [system] = useState(() => (readPlatform().desktop ? 'Match device' : THEME_LABEL.system));
	const choose = (theme: Theme) => {
		settings().set('theme', theme);
		track('settings_changed', { key: 'theme', value: theme });
	};
	return (
		<div className="mn-row set theme-set">
			<span className="mn-lb">
				<span id={`${id}-l`}>Theme</span>
			</span>
			<Segmented
				options={THEMES.map((theme) => ({ value: theme, label: theme === 'system' ? system : THEME_LABEL[theme] }))}
				value={value}
				onChange={choose}
				labelledBy={`${id}-l`}
			/>
		</div>
	);
}

function SettingsBody({ onInstall, onNotify }: { onInstall: () => void; onNotify: () => void }) {
	const current = useSettings();
	const flip = (key: TrackedSwitch) => {
		const value = !current[key];
		settings().set(key, value);
		track('settings_changed', { key, value });
	};
	return (
		<>
			<ul className="mn-list sets">
				<li>
					<ThemeRow value={current.theme} />
				</li>
				{SWITCHES.map((s) => (
					<li key={s.key}>
						<SwitchRow label={s.label} sub={s.sub} checked={current[s.key]} onToggle={() => flip(s.key)} />
					</li>
				))}
				<li>
					{/* Never tracked: turning counts off sends nothing to say so (platform/consent.ts). */}
					<SwitchRow
						label="Count my plays"
						sub="Anonymous counts of how Sparrow is used, under a random id. Never your answers."
						checked={current.analytics}
						onToggle={() => setUsageCounts(!current.analytics)}
						testId="settings-analytics"
					/>
				</li>
			</ul>
			<NotificationSettings onInstall={onInstall} onOpenSheet={onNotify} />
			<p className="mn-foot">
				<PrivacyLink />
			</p>
		</>
	);
}

/** To /privacy, leaving the sheet's history entry first (navigate does that). */
function PrivacyLink({ label = 'Privacy' }: { label?: string }) {
	return (
		<a
			className="tlink mn-link"
			href="/privacy"
			onClick={(e) => {
				if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
				e.preventDefault();
				navigate('/privacy');
			}}
		>
			{label}
		</a>
	);
}

function About() {
	return (
		<div className="about">
			<p className="ab-lead">Three new Arrowwords every day, at Easy, Medium and Hard. Free, no sign-up.</p>
			<p>Your games, streak and stats are kept on this device.</p>
			<p>
				<PrivacyLink label="How Sparrow handles your data" />
			</p>
			<p className="mn-foot">
				<span>Made by Pixel Time Ltd.</span>
				<span>Version {APP_VERSION}</span>
			</p>
		</div>
	);
}
